import { Icon } from "@mdi/react";
import { useState } from "react";

import { mdiFactory } from "@mdi/js";
import { Button, Checkbox, Divider, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FilterDropdownProps, SortOrder } from "antd/lib/table/interface";

import { Dashboard, Workspace } from "models/workspace";

import { getTableTitleMarkup } from "components/org-settings/OrganizationDisplaySettings";
import { TableContainer } from "components/user-settings/WorkspacesList";

import { UseAddDashboardToWorkspaceReturnT } from "../hooks/useAddDashboardToWorkspace";
import { useDashboardHighlight } from "../hooks/useDashboardHighlight";
import { UseDeleteDashboardReturnT } from "../hooks/useDeleteDashboard";
import { UseHideDashboardReturnT } from "../hooks/useHideDashboard";
import "./DashboardTable.scss";
import {
  DashboardRowListT,
  MIDSTREAM_WIDGETS,
  TAG_ORDER,
  TYPE_ORDER,
  getAvailableWidgetTypes,
  getWidgetTags
} from "./DashboardTable.util";
import { DashboardTableActions } from "./DashboardTableActions";

/**
 * In the cases of our dashboard tables, this value works with three actions as the minumum.
 * Since we always have two dashboard tables, and one with three, we can use this as a minimum,
 * to keep both dashboard tables better in allignment with each other.
 */
const MINIMUM_ACTIONS_COLUMN_WIDTH = 116;

export type DashboardTablePropsT = {
  dashboardList: Dashboard[];
  /** Set to null to disable default sorting. Type column is default sort.*/
  defaultSort?: {
    columnKey: string;
    order: SortOrder;
  } | null;
  hideDashboard?: UseHideDashboardReturnT["mutate"];
  addDashboard?: UseAddDashboardToWorkspaceReturnT["mutate"];
  deleteDashboard?: UseDeleteDashboardReturnT["mutate"];
  cloneDashboard?: (dashboard: Dashboard, title: string, type: string) => Promise<void>;
  tableSettings: {
    pagination?: {
      pageSize: number;
      showSizeChanger: boolean;
      hideOnSinglePage: boolean;
    };
    title: string;
  };
  workspace: Workspace;
  highlightedDashboardId?: string | null;
  setHighlightedDashboard?: (id: string | null) => void;
};

export default function DashboardTable(props: DashboardTablePropsT) {
  const {
    addDashboard,
    dashboardList,
    defaultSort: propDefaultSort,
    hideDashboard,
    deleteDashboard,
    cloneDashboard,
    tableSettings,
    workspace,
    highlightedDashboardId,
    setHighlightedDashboard
  } = props;

  // If propDefaultSort is undefined, default to type; if null, no default sort
  const defaultSort =
    propDefaultSort === undefined
      ? { columnKey: "type", order: "ascend" as SortOrder }
      : propDefaultSort;

  const [widgetTypeFilter, setWidgetTypeFilter] = useState<string[]>([]);

  // Use dashboard highlight hook for clean highlight management
  const { getRowClassName } = useDashboardHighlight({
    highlightedDashboardId,
    setHighlightedDashboard
  });

  const dashboardRowList: DashboardRowListT = dashboardList.map((dashboard, index) => {
    if (!dashboard)
      return {
        key: `dashboard${index}`,
        title: "Dashboard Error (Not found)",
        type: "",
        widgets: [],
        actions: null
      };

    return {
      key: dashboard.dashboardId,
      title: dashboard.title,
      type: dashboard.type,
      widgets: getWidgetTags(dashboard.widgets),
      actions: (
        <DashboardTableActions
          dashboard={dashboard}
          workspace={workspace}
          addDashboard={addDashboard ? () => addDashboard(workspace, dashboard) : null}
          hideDashboard={
            hideDashboard ? () => hideDashboard(dashboard.dashboardId, workspace) : null
          }
          deleteDashboard={
            deleteDashboard
              ? () => deleteDashboard(dashboard.dashboardId, workspace)
              : null
          }
          cloneDashboard={
            cloneDashboard
              ? (title: string, type: string) => cloneDashboard(dashboard, title, type)
              : undefined
          }
        />
      )
    };
  });

  const filteredDashboardData = widgetTypeFilter.length
    ? dashboardRowList.filter((record) =>
        record.widgets.some((widget) => widgetTypeFilter.includes(widget.id))
      )
    : dashboardRowList;

  const filterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    close
  }: FilterDropdownProps): JSX.Element => {
    // Filter out the widgets that are not in the selected dashboard
    const availableWidgetTypes = getAvailableWidgetTypes(dashboardRowList);

    const renderWidgetItems = (items: string[]) =>
      items.map((id) => {
        if (id === "Midstream") {
          // Render the Midstream submenu
          return (
            <div key="Midstream" className="widget-type-list_submenu">
              <Divider style={{ margin: 0 }} />
              <div className="widget-type-list_submenu-title">Midstream</div>
              <div>{renderWidgetItems(MIDSTREAM_WIDGETS)}</div>
              <Divider style={{ margin: 0 }} />
            </div>
          );
        }

        const widget = availableWidgetTypes.find((widget) => widget.id === id);
        if (!widget) return null;

        const isSelected = selectedKeys.includes(widget.id);

        return (
          <div
            className={`widget-type-list_item ant-dropdown-menu-item ${
              isSelected ? "ant-dropdown-menu-item-selected" : ""
            }`}
            key={widget.id}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              const newKeys = isSelected
                ? selectedKeys.filter((t) => t !== widget.id)
                : [...selectedKeys, widget.id];

              setSelectedKeys(newKeys);
            }}>
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                e.preventDefault();

                const newKeys = e.target.checked
                  ? [...selectedKeys, widget.id]
                  : selectedKeys.filter((t) => t !== widget.id);

                setSelectedKeys(newKeys);
              }}
            />
            {widget.type}
          </div>
        );
      });

    return (
      <div className="widget-type-list_wrapper">
        <div className="widget-type-list_items">{renderWidgetItems(TAG_ORDER)}</div>
        <Divider style={{ margin: 0 }} />
        <div className="widget-type-list_footer">
          <Button
            type="link"
            title="Reset"
            size="small"
            onClick={() => {
              setSelectedKeys([]);
            }}>
            Reset
          </Button>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "8px" }}
            onClick={() => {
              setWidgetTypeFilter(selectedKeys as string[]);
              confirm();
              close();
            }}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  const columns: ColumnsType<DashboardRowListT[number]> = [
    {
      title: "Dashboard",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
      defaultSortOrder:
        defaultSort?.columnKey === "title" ? defaultSort.order : undefined,
      render: (title: string, record: DashboardRowListT[number]) => {
        const hasMidstreamWidgets = record.widgets.some((widget) =>
          MIDSTREAM_WIDGETS.includes(widget.id)
        );

        return (
          <span>
            {hasMidstreamWidgets && (
              <Icon style={{ marginRight: "4px" }} path={mdiFactory} size={0.8} />
            )}
            {title}
          </span>
        );
      }
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: Object.keys(TYPE_ORDER).map((type) => ({
        text: type,
        value: type
      })),
      onFilter: (value: string | number, record: DashboardRowListT[number]) => {
        const type = record.type || "Unknown";
        return type === value;
      },
      sorter: (a, b) => TYPE_ORDER[a.type] - TYPE_ORDER[b.type],
      defaultSortOrder: defaultSort?.columnKey === "type" ? defaultSort.order : undefined
    },
    {
      title: "Widgets",
      dataIndex: "widgets",
      key: "widgets",

      filterDropdown: filterDropdown,
      render: (widgets: { id: string; type: string; count: number; color: string }[]) => (
        <div className="widget-type-tag-container">
          {widgets.map((widget) => {
            const isMidstream = MIDSTREAM_WIDGETS.includes(widget.id);

            return (
              <Tag
                style={
                  isMidstream
                    ? {
                        color: widget.color,
                        borderColor: widget.color,
                        backgroundColor: "transparent"
                      }
                    : {}
                }
                color={widget.color}
                key={widget.id}
                className={isMidstream ? "midstream-tag" : ""}>
                {widget.type} {widget.count > 1 ? `x${widget.count}` : ""}
              </Tag>
            );
          })}
        </div>
      )
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: MINIMUM_ACTIONS_COLUMN_WIDTH,
      align: "left" as const
    }
  ];

  return (
    <>
      {getTableTitleMarkup(tableSettings.title)}
      <TableContainer className="dashboard-table-container">
        <Table
          columns={columns}
          dataSource={filteredDashboardData}
          pagination={tableSettings.pagination || false}
          size="middle"
          rowKey={(record) => record.key}
          rowClassName={(record) => getRowClassName(record.key)}
        />
      </TableContainer>
    </>
  );
}
